<template>

  <div>
    <Modal
      :title="isNew ? t('titles.add_product') : t('titles.edit_product')"
      :submit-button="canEditProducts"
      :submit="submit"
      :submitting="submitting"
    >

      <template v-if="$can('organizations.manage')">

        <o-autocomplete
          v-if="isNew"
          label="Organization"
          v-model.number="v$.organizationID.$model"
          :validator="v$.organizationID"
          :items="Organization.getOptions(true)"
        />

        <o-text-field
          v-if="!isNew"
          label="Organization"
          readonly
          :model-value="props.product.organization ? props.product.organization.name : $t('misc.unknown_item')"
        />

      </template>

      <o-text-field
        :label="$t('titles.product_name')"
        v-model="v$.name.$model"
        :validator="v$.name"
        :readonly="!canEditProducts"
      />

      <v-row>

        <v-col cols="12" sm="6">
          <o-select
            :label="$t('titles.product_type')"
            v-model="v$.productType.$model"
            :validator="v$.productType"
            :items="ProductTypeRegistry.getOptions()"
            :readonly="!canEditProducts"
          />
        </v-col>

        <v-col cols="12" sm="6">
          <o-select
            :label="$t('titles.vertical')"
            v-model="v$.verticalType.$model"
            :validator="v$.verticalType"
            :items="VerticalRegistry.getOptions()"
            :readonly="!canEditProducts"
          />
        </v-col>

      </v-row>

      <template v-if="selectedVertical?.supports(['entryAndExitHours'])">

        <v-row>
          
          <v-col cols="12" sm="6">
            <o-select
              :label="$t('titles.entry_hours_before_event')"
              v-model.number="v$.entryHoursBeforeEvent.$model"
              :validator="v$.entryHoursBeforeEvent"
              :items="timeOffsetOptions"
              :readonly="!canEditProducts"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <o-select
              v-model.number="v$.exitHoursAfterEvent.$model"
              :validator="v$.exitHoursAfterEvent"
              :label="$t('titles.exit_hours_after_event')"
              :items="timeOffsetOptions"
              :readonly="!canEditProducts"
            />
          </v-col>

        </v-row>

      </template>

      <template v-if="selectedVertical?.supports(['limitedByWeekday'])" class="mb-4">

        <o-select
          :label="$t('titles.weekdays')"
          v-model="v$.days.$model"
          multiple
          :validator="v$.days"
          :items="dayOptions"
          @update:modelValue="daysChanged"
          :readonly="!canEditProducts"
        />

      </template>

      <template v-if="selectedVertical?.supports(['startAndEndTimes'])">

        <v-row>

          <v-col cols="12" sm="4">
            <o-text-field
              :label="$t('titles.first_available_start_time')"
              type="time"
              v-model="v$.firstStartTime.$model"
              :validator="v$.firstStartTime"
              :readonly="!canEditProducts"
              clearable
            />
          </v-col>

          <v-col cols="12" sm="4">
            <o-text-field
              :label="$t('titles.last_available_start_time')"
              type="time"
              v-model="v$.lastStartTime.$model"
              :validator="v$.lastStartTime"
              :readonly="!canEditProducts"
              clearable
            />
          </v-col>

          <v-col cols="12" sm="4">
            <o-text-field
              :label="$t('titles.end_time')"
              type="time"
              v-model="v$.endTime.$model"
              :validator="v$.endTime"
              :readonly="!canEditProducts"
              clearable
            />
          </v-col>

        </v-row>

      </template>

    </Modal>
  </div>

</template>

<script setup lang="ts">

import { computed, inject, onBeforeMount, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'

// models
import Organization from '../../../models/Organization'
import Product, { type UpsertProductRequest } from '../../../models/Product'

// validation
import useVuelidate from '@vuelidate/core'
import { required, minValue } from '../../../utils/i18n-validators'

// misc
import NotificationService from '../../../services/NotificationService'
import timeOffsetOptions from '../../../utils/timeOffsetOptions'
// @ts-ignore
import { closeDialog } from 'vue3-promise-dialog'
import ProductTypeRegistry from '../../../product_types/ProductTypeRegistry'
import VerticalRegistry from '../../../verticals/VerticalRegistry'
import type { Can } from '../../../main'
import type AbstractVertical from '../../../verticals/AbstractVertical'

interface Props {
  product: Product
}

const props = defineProps<Props>()

const isNew = !props.product.ID
const { t } = useI18n()

const $can = inject('$can') as Can
const canEditProducts = $can('products.write')

/**
 * Dropdown options
 */

const dayOptions = [
  {value: 'sun', title: t('enums.weekdays.sun')},
  {value: 'mon', title: t('enums.weekdays.mon')},
  {value: 'tue', title: t('enums.weekdays.tue')},
  {value: 'wed', title: t('enums.weekdays.wed')},
  {value: 'thu', title: t('enums.weekdays.thu')},
  {value: 'fri', title: t('enums.weekdays.fri')},
  {value: 'sat', title: t('enums.weekdays.sat')},
]

const dayMap = {
  daysSun: 'sun',
  daysMon: 'mon',
  daysTue: 'tue',
  daysWed: 'wed',
  daysThu: 'thu',
  daysFri: 'fri',
  daysSat: 'sat',
}

/**
 * Form state and rules
 */

interface FormState {
  ID: null | number
  organizationID: null | number
  verticalType: null | string
  productType: null | string
  name: string
  entryHoursBeforeEvent: number
  exitHoursAfterEvent: number
  days: string[]
  firstStartTime: null | string
  lastStartTime: null | string
  endTime: null | string
}

const formState = reactive<FormState>({
  ID: props.product.ID,
  organizationID: props.product.organizationID,
  verticalType: props.product.verticalType,
  productType: props.product.productType,
  name: props.product.name,
  entryHoursBeforeEvent: props.product.entryHoursBeforeEvent,
  exitHoursAfterEvent: props.product.exitHoursAfterEvent,
  days: [],
  // TODO: do I need to manually trim off the seconds here?
  firstStartTime: props.product.firstStartTime,
  lastStartTime: props.product.lastStartTime,
  endTime: props.product.endTime,
})

onBeforeMount(() => {
  if (props.product.daysSun) { formState.days.push('sun')}
  if (props.product.daysMon) { formState.days.push('mon')}
  if (props.product.daysTue) { formState.days.push('tue')}
  if (props.product.daysWed) { formState.days.push('wed')}
  if (props.product.daysThu) { formState.days.push('thu')}
  if (props.product.daysFri) { formState.days.push('fri')}
  if (props.product.daysSat) { formState.days.push('sat')}
})

const selectedVertical = computed<null | AbstractVertical>(() => {
  if (!formState.verticalType) {
    return null
  }
  return VerticalRegistry.get(formState.verticalType) ?? null
})

// TODO (hygiene): We should use conditional rules here, instead of a computed() (and we're moving away from Vuelidate anyway)
const formRules = computed(() => {

  const rules = {
    organizationID: {
      required,
    },
    verticalType: {
      required,
    },
    productType: {
      required,
    },
    name: {
      required,
    },
    entryHoursBeforeEvent: {},
    exitHoursAfterEvent: {},
    days: {},
    firstStartTime: {},
    lastStartTime: {},
    endTime: {},
  }

  if (selectedVertical.value?.supports(['entryAndExitHours'])) {
    
    rules.entryHoursBeforeEvent = {
      required,
      minValue: minValue(0)
    }

    rules.exitHoursAfterEvent = {
      required,
      minValue: minValue(0)
    }

  }

  if (selectedVertical.value?.supports(['limitedByWeekday'])) {
    rules.days = {
      required,
    }
  }

  return rules

})

const v$ = useVuelidate(formRules, formState)

/**
 * Submission
 */

const submitting = ref(false)

async function submit() {

  const isValid = await v$.value.$validate()

  if (!isValid || !canEditProducts) {
    return
  }

  submitting.value = true

  // Depending on which vertical is selected, we may not want to send all of
  // the fields to the API. Build the payload accordingly.

  const payload: UpsertProductRequest = {
    organizationID: formState.organizationID!,
    verticalType: formState.verticalType!,
    productType: formState.productType!,
    name: formState.name,
    days: {},
  }

  if (selectedVertical.value?.supports(['entryAndExitHours'])) {
    payload.entryHoursBeforeEvent = formState.entryHoursBeforeEvent
    payload.exitHoursAfterEvent = formState.exitHoursAfterEvent
  }

  if (selectedVertical.value?.supports(['limitedByWeekday'])) {
    for (const [_fieldName, formValue] of Object.entries(dayMap)) {
      payload.days[formValue] = formState.days.includes(formValue)
    }
  }

  if (selectedVertical.value?.supports(['startAndEndTimes'])) {
    payload.firstStartTime = normalizeTime(formState.firstStartTime)
    payload.lastStartTime = normalizeTime(formState.lastStartTime)
    payload.endTime = normalizeTime(formState.endTime)
  }

  const savePromise = isNew ? Product.saveNew(payload) : Product.saveExisting(props.product.ID, payload)

  savePromise
    .then((_updatedProduct) => {
      const msg = isNew ? t('alerts.product_created') : t('alerts.product_updated')
      NotificationService.success(msg)
      closeDialog(null)
    })
    .catch((error) => {
      console.error(error)
      NotificationService.error(t('alerts.couldnt_save_product'))
    })
    .finally(() => {
      submitting.value = false
    })

}

function daysChanged(selectedDays: string[]) {
  console.log('selectedDays:', selectedDays)
  // When the user changes the value of the "Days" control, make sure the
  // selected values are displayed in chronological order.
  selectedDays.sort((day1, day2) => {
    const day1Index = findDayIndex(day1)
    const day2Index = findDayIndex(day2)
    if (day1Index > day2Index) {
      return 1
    } else if (day1Index < day2Index) {
      return -1
    }
    return 0 // this should never happen, buuut
  })
  formState.days = selectedDays
}

function findDayIndex(day: string) {
  return dayOptions.findIndex((dayOption) => {
    return dayOption.value === day
  })
}

/**
 * The Product model (and the API) represent the time fields in hh:mm:ss
 * format, but selecting a time value on the form produces a value in
 * hh:mm format (in some browsers, at least). Fix that.
 * 
 * Also convert empty strings to nulls.
 */
function normalizeTime(time: null | string): null | string {
  if (!time) {
    return null
  }
  if (/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/.test(time)) {
    time += ':00'
  }
  return time
}

</script>
